import { NgClass, NgStyle } from '@angular/common';
import { Component, computed, EventEmitter, input, Input, Output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ICON_NAME_MAPPING, isRegisteredLucideIcon } from '@calio-lib/calio-lucide-icon/lucide-icons.config';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { LucideAngularModule } from 'lucide-angular';

/**
 * Calio Mat Icon Component
 *
 * A unified icon component that supports both legacy SVG icons and Lucide icons.
 * If the iconType has a Lucide mapping, it renders a Lucide icon.
 * Otherwise, it falls back to the original SVG icon from MatIcon registry.
 *
 * @example
 * ```html
 * <!-- Uses Lucide icon (if mapped) -->
 * <calio-mat-icon iconType="edit" height="24px" width="24px" />
 *
 * <!-- Falls back to SVG (custom icons like logos) -->
 * <calio-mat-icon iconType="calenso-violet-logo" height="40px" width="auto" />
 * ```
 */
@Component({
  selector: 'calio-mat-icon',
  templateUrl: './calio-mat-icon.component.html',
  styleUrls: ['./calio-mat-icon.component.scss'],
  imports: [MatIcon, NgStyle, NgClass, NgbTooltipModule, LucideAngularModule],
})
export class CalioMatIconComponent {
  /** Icon type identifier */
  iconType = input.required<string>();

  /** Additional CSS classes */
  @Input() customClass: string;

  /** Icon height (e.g., '24px', 'auto') */
  @Input() height: string;

  /** Icon width (e.g., '24px', 'auto') */
  @Input() width: string;

  /** SVG fill color (for legacy icons) */
  @Input() fill: string;

  /** Tooltip text */
  @Input() tooltip: string;

  /** Tooltip placement */
  @Input() placement: string;

  /** Click event emitter */
  @Output() clickEvent = new EventEmitter<void>();

  /** Resolve the Lucide icon name, or null when this should render the legacy SVG. */
  readonly lucideIconName = computed(() => {
    const type = this.iconType();
    // The alias table wins first, so every existing call site keeps the exact icon it
    // has today ('dashboard' stays layout-dashboard, 'close' stays x). Only when there
    // is no alias do we ask whether Lucide itself knows the name — that is what makes
    // the ~75 registered-but-unaliased icons usable instead of silently rendering
    // nothing. Anything Lucide does not know still falls through to the SVG branch,
    // which is how brand logos like 'calenso-violet-logo' keep working.
    return ICON_NAME_MAPPING[type] ?? (isRegisteredLucideIcon(type) ? type : null);
  });

  /** Get numeric size from height/width for Lucide */
  readonly lucideSize = computed(() => {
    const h = this.height || '24px';
    const match = h.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 24;
  });

  /** Check if we should use Lucide icon */
  readonly useLucide = computed(() => !!this.lucideIconName());
}
